import { useEffect, useState, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import { useLang } from "@/context/LangContext";
import { Button } from "@/components/ui/button";
import { CheckCircle2, XCircle, Loader2 } from "lucide-react";

export default function PaymentReturn({ success }) {
  const { t } = useLang();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const [state, setState] = useState(success ? "checking" : "cancelled");
  const attempts = useRef(0);

  useEffect(() => {
    if (!success) return;
    const sessionId = params.get("session_id");
    if (!sessionId) { setState("error"); return; }
    let timer;
    const poll = async () => {
      attempts.current += 1;
      try {
        const { data } = await api.get(`/payments/status/${sessionId}`);
        if (data.payment_status === "paid") { setState("paid"); return; }
        if (data.payment_status === "expired" || attempts.current > 12) { setState("error"); return; }
      } catch {
        if (attempts.current > 12) { setState("error"); return; }
      }
      timer = setTimeout(poll, 2000);
    };
    poll();
    return () => clearTimeout(timer);
  }, [success, params]);

  return (
    <div className="min-h-screen flex items-center justify-center p-6">
      <div className="max-w-sm w-full text-center ss-rise">
        {state === "checking" && (
          <>
            <Loader2 className="w-14 h-14 mx-auto text-primary animate-spin mb-4" />
            <p className="text-muted-foreground">{t("verifying_payment")}</p>
          </>
        )}
        {state === "paid" && (
          <>
            <CheckCircle2 className="w-16 h-16 mx-auto text-emerald-400 mb-4" />
            <h1 className="font-display text-2xl font-bold mb-2">{t("payment_success")}</h1>
          </>
        )}
        {(state === "cancelled" || state === "error") && (
          <>
            <XCircle className="w-16 h-16 mx-auto text-red-400 mb-4" />
            <h1 className="font-display text-2xl font-bold mb-2">{t("payment_cancelled")}</h1>
          </>
        )}
        <Button data-testid="back-to-app-btn" onClick={() => navigate("/")} className="mt-4 rounded-xl bg-primary hover:bg-primary/90 font-semibold">
          {t("back_to_app")}
        </Button>
      </div>
    </div>
  );
}
